import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { AlertCircle } from 'lucide-react';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * `<SubmitForm>` — UI-SPEC § 3.8 single-input glass-strong form.
 *
 * Slots:
 * - h1 title + subhead
 * - <label> + <Input> with validation message (8..500 chars after trim)
 * - Submit button: "Start Research" / "Submitting…" disabled while pending
 * - Error panel (coral-bordered) on failed mutation with optional Retry
 *
 * Behaviour:
 * - Auto-focuses input on mount.
 * - Validation message shows after first blur AND when input is non-empty
 *   AND invalid (so the user isn't yelled at on first paint).
 * - Honors `initialAddress` prefill (used by JobErrorPanel resubmit link
 *   which navigates to `/submit?address=<encoded>`).
 * - `readOnly` while pending — prevents in-flight edits but keeps the
 *   text visible (vs `disabled` which dims).
 *
 * Bronze discipline: this is the one user-facing primary CTA in the
 * jobs domain that DOES use `bg-bronze-500` (it's the conversion point;
 * the bronze reserved-list slot for "primary submit" applies here per
 * UI-SPEC § 1).
 */
export interface SubmitFormProps {
  /** Optional prefill from ?address= query param (JobErrorPanel resubmit). */
  initialAddress?: string;
  isPending: boolean;
  isError: boolean;
  errorMessage?: string;
  onSubmit: (address: string) => void;
  onRetry?: () => void;
}

const MIN_LEN = 8;
const MAX_LEN = 500;

export function SubmitForm({
  initialAddress = '',
  isPending,
  isError,
  errorMessage,
  onSubmit,
  onRetry,
}: SubmitFormProps) {
  const titleId = useId();
  const validationId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [address, setAddress] = useState(initialAddress);
  const [hasBlurred, setHasBlurred] = useState(false);

  // Auto-focus on mount.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // If initialAddress arrives late (e.g. ?address= late-bound), update once.
  useEffect(() => {
    if (initialAddress) setAddress(initialAddress);
  }, [initialAddress]);

  const trimmed = address.trim();
  const isValid = trimmed.length >= MIN_LEN && trimmed.length <= MAX_LEN;
  const showValidation = hasBlurred && !isValid && trimmed.length > 0;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isValid || isPending) return;
    onSubmit(trimmed);
  };

  return (
    <GlassPanel
      variant="strong"
      as="form"
      aria-labelledby={titleId}
      onSubmit={handleSubmit}
      className={cn(
        'p-6 md:p-8 max-w-[640px] mx-auto flex flex-col gap-6',
      )}
    >
      <div className="flex flex-col gap-2">
        <h1
          id={titleId}
          className="text-xl font-semibold text-foam-50"
        >
          Research a Property
        </h1>
        <p className="text-base text-foam-50/70">
          Enter an address to launch the House Hunter agents. The pipeline runs
          7 parallel agents and compiles a single report.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <label
          htmlFor="submit-address"
          className="text-sm font-semibold text-foam-50"
        >
          Property Address
        </label>
        <Input
          id="submit-address"
          ref={inputRef}
          type="text"
          placeholder="1234 Main St, Tampa, FL 33601"
          required
          minLength={MIN_LEN}
          maxLength={MAX_LEN}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onBlur={() => setHasBlurred(true)}
          readOnly={isPending}
          aria-describedby={validationId}
          aria-invalid={showValidation || undefined}
          autoComplete="street-address"
        />
        <span
          id={validationId}
          aria-live="polite"
          className={cn(
            'text-sm text-coral-100 min-h-[1.25rem]',
            showValidation ? 'opacity-100' : 'opacity-0',
          )}
        >
          {showValidation
            ? `Address must be ${MIN_LEN}–${MAX_LEN} characters.`
            : ''}
        </span>
      </div>

      <Button
        type="submit"
        disabled={!isValid || isPending}
        aria-busy={isPending || undefined}
        aria-disabled={!isValid || isPending || undefined}
        className={cn(
          'self-start w-full md:w-auto',
          'h-12 px-6 rounded-2xl font-semibold',
          'bg-bronze-500 text-ocean-950 hover:bg-bronze-500/90',
          'disabled:bg-foam-50/15 disabled:text-foam-50/50 disabled:cursor-not-allowed',
          'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
        )}
      >
        {isPending ? 'Submitting…' : 'Start Research'}
      </Button>

      {isError ? (
        <GlassPanel
          variant="strong"
          as="div"
          role="alert"
          className="p-4 border-coral-500/40 flex items-start gap-3"
        >
          <AlertCircle
            className="w-5 h-5 text-coral-500 shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <div className="flex flex-col gap-2 min-w-0">
            <h2 className="text-sm font-semibold text-foam-50">
              Couldn&apos;t submit.
            </h2>
            <p className="text-sm text-foam-50/80 break-words">
              {errorMessage || 'Try again or check the server logs.'}
            </p>
            {onRetry ? (
              <button
                type="button"
                onClick={onRetry}
                className={cn(
                  'self-start text-sm font-semibold text-tide-400 hover:text-tide-400/80',
                  'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 rounded',
                )}
              >
                Retry
              </button>
            ) : null}
          </div>
        </GlassPanel>
      ) : null}
    </GlassPanel>
  );
}
